// src/components/PlantDashboard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    TextField,
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Alert,
    Grid,
    FormControl, // Importar FormControl
    InputLabel,   // Importar InputLabel
    Select,      // Importar Select
    MenuItem,    // Importar MenuItem
} from '@mui/material';

import {
    StyledMainContainer,
    StyledHeaderWrapper,
    StyledPageTitle,
    StyledTableContainer,
    StyledTableHeaderCell,
    StyledPaper,
} from './StyleComponents';

import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';

import { useNavigate } from 'react-router-dom';
import Header from './Header';
import api from './api';

// Importaciones para el Date Picker
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css'; // Importa los estilos básicos de react-day-picker


const Maps_API_KEY = 'AIzaSyB9MUmEysYovfNTHDxVZoQo8cEVoVvDsmw';

const libraries = ['places'];


function HomeDashboard() {
    const navigate = useNavigate();
    const [direcciones, setDirecciones] = useState([]);
    const [rutinas, setRutinas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    //Estados para los filtros
    const [tiposRutina, setTiposRutina] = useState([]);
    const [clientes, setClientes] = useState([]);

    // Estado para los filtros seleccionados (valores que se usan en la consulta)
    const [selectedFilters, setSelectedFilters] = useState({
        selectedDate: new Date(),
        tipoRutina: '',
        cliente: '',
    });

    // Estados adicionales para la UI
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [stats, setStats] = useState({ verde: 0, rojo: 0, amarillo: 0, pendiente: 0 });
    const [searchTerm, setSearchTerm] = useState('');



    // --- Configuración del Mapa ---
    // Estilo del contenedor del mapa
    const mapContainerStyle = {
        width: '100%',
        height: '400px', // O la altura que prefieras para que se vea bien
        borderRadius: '8px',
    };

    // Centro inicial del mapa (puedes ajustarlo a una ubicación más relevante)
    const defaultCenter = useMemo(() => ({
        lat: 24.4326, // Latitud de la Ciudad de México como ejemplo
        lng: -102.1432, // Longitud de la Ciudad de México
    }), []); // useMemo para que no se recalcule en cada render

    // Hook para cargar la API de Google Maps
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: Maps_API_KEY,
        libraries: libraries,
    });

    // Función auxiliar para formatear la fecha a YYYY-MM-DD
    const formatDateToYYYYMMDD = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Meses son 0-indexados
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Función para obtener el color del semáforo
    const getSemaforoColor = (status) => {
        switch (status) {
            case 'verde':
                return 'https://storage.googleapis.com/imagenes_90/semaforo_rutinas_verde.png';
            case 'rojo':
                return 'https://storage.googleapis.com/imagenes_90/semaforo_rutinas_rojo.png';
            case 'amarillo':
                return 'https://storage.googleapis.com/imagenes_90/semaforo_rutinas_amarillo.png';
            default:
                return 'https://storage.googleapis.com/imagenes_90/semaforo_rutinas_blanco.png'; // Color para "pendiente"
        }
    };

    const filteredRutinas = useMemo(() => {
        if (!searchTerm) {
            return rutinas;
        }

        const lowercasedSearchTerm = searchTerm.toLowerCase();

        return rutinas.filter(rutina => {
            // Busca coincidencias en los campos relevantes: nombre de la planta, nombre de la rutina y nombre del coordinador.
            const plantaNombre = rutina.planta?.nombre?.toLowerCase() || '';
            const rutinaNombre = rutina.nombre?.toLowerCase() || '';
            const coordinadorNombre = rutina.coordinador?.nombre?.toLowerCase() || '';
            const semaforo = rutina.semaforo?.toLowerCase() || '';
            const status = rutina.status?.toLowerCase() || ''

            return (
                plantaNombre.includes(lowercasedSearchTerm) ||
                rutinaNombre.includes(lowercasedSearchTerm) ||
                coordinadorNombre.includes(lowercasedSearchTerm) ||
                semaforo.includes(lowercasedSearchTerm) ||
                status.includes(lowercasedSearchTerm)
            );
        });
    }, [rutinas, searchTerm]);



    // Función para cargar los datos de plantas y estadísticas desde la API
    const fetchPlantData = useCallback(async (filters) => {
        setIsLoading(true);
        setError(null);
        //console.log(filters.selectedDate)
        //console.log(filters.tipoRutina)
        //console.log(filters.cliente)

        try {
            // dentro de fetchPlantData()
            const tiposRutinaResponse = await api.get(`/tipos_rutinas/nombres`);
            const allowed = new Set(['Diaria calidad', 'Diaria mantenimiento']);
            const tiposFiltrados = Array.from(new Set(
            (tiposRutinaResponse.data || []).filter(n => allowed.has(n))
            ));
            setTiposRutina(tiposFiltrados);

            const clientesResponse = await api.get(`/clientes/?skip=0&limit=1000`);
            setClientes(clientesResponse.data);

            // Construir los parámetros de la URL para los filtros de rutinas filtradas
            const formattedDate = formatDateToYYYYMMDD(filters.selectedDate);
            const params = new URLSearchParams();
            if (formattedDate) {
                //params.append('date', formattedDate);
                params.append('created_at', formattedDate);
            }
            if (filters.tipoRutina) {
                //console.log(filters.tipoRutina)
                params.append('tipo_rutina_nombre', filters.tipoRutina);
            }
            params.append('skip', '0');
            params.append('limit', '1000');

            //Consultar rutinas filtradas
            const rutinasResponse = await api.get(`/rutinas/filtradas_nombre/?${params.toString()}`);
            //console.log('Rtinas filtradas')
            //console.log(rutinasResponse.data);
            //console.log('cliente')
            //console.log(filters.cliente)
            // Filtrar por cliente si está seleccionado
            let filteredRutinas = rutinasResponse.data;
            if (filters.cliente) {
                filteredRutinas = rutinasResponse.data.filter(
                    (rutina) => rutina.planta.cliente_id === filters.cliente
                );
            }
            //console.log(filteredRutinas)
            setRutinas(filteredRutinas);
            //setRutinas(rutinasResponse.data)

            //Obtener los IDs de las plantas de las rutinas filtradas
            const plantaIdsFiltradas = new Set(filteredRutinas.map((rutina) => rutina.planta_id));

            // 4. Obtener las direcciones de las plantas y filtrarlas
            const direccionesResponse = await api.get('/direcciones_plantas/?skip=0&limit=1000');
            const filteredDirecciones = direccionesResponse.data.filter(
                (direccion) => plantaIdsFiltradas.has(direccion.planta_id)
            );
            console.log('Direcciones filtradas')
            //console.log(filteredDirecciones)
            setDirecciones(filteredDirecciones);
            //setDirecciones(direccionesResponse.data);
            // Calcular las nuevas estadísticas de semáforos
            const newStats = { verde: 0, rojo: 0, amarillo: 0, pendiente: 0 };
            filteredRutinas.forEach(rutina => {
                if (rutina.status === 'respondida') {
                    switch (rutina.semaforo) {
                        case 'verde':
                            newStats.verde++;
                            break;
                        case 'rojo':
                            newStats.rojo++;
                            break;
                        case 'amarillo':
                            newStats.amarillo++;
                            break;
                    }
                }

            });

            filteredRutinas.forEach(rutina => {
                if (rutina.status === 'pendiente') {
                    newStats.pendiente++;
                }
            })
            setStats(newStats);


        } catch (err) {
            console.error('Error al cargar datos de plantas:', err);
            setError('No se pudieron cargar los datos de las plantas.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Función para manejar el clic del botón "Consultar"
    const handleConsultarClick = () => {
        // Al hacer clic, se usan los valores del estado `selectedFilters`
        fetchPlantData(selectedFilters);
        setIsCalendarOpen(false);
    };

    // Efecto para la carga inicial de datos (se ejecuta solo una vez)
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                // Obtener tipos de rutina y clientes
                const tiposRutinaResponse = await api.get(`/tipos_rutinas/nombres`);
                // Filtrar a solo las permitidas y deduplicar
                const allowed = new Set(['Diaria calidad', 'Diaria mantenimiento']);
                const tiposFiltrados = Array.from(new Set(
                (tiposRutinaResponse.data || []).filter(n => allowed.has(n))
                ));
                setTiposRutina(tiposFiltrados);

                const clientesResponse = await api.get(`/clientes/?skip=0&limit=1000`);
                setClientes(clientesResponse.data);

                // Establecer el valor por defecto del tipo de rutina
                // Defaults correctos
                const defaultRoutineName = tiposFiltrados.includes('Diaria calidad') ? 'Diaria calidad' : (tiposFiltrados[0] || '');
                const defaultClient = clientesResponse.data.find(c => c.nombre === '');
                const defaultClientId = defaultClient ? defaultClient.id : '';


                // Actualizar los filtros con los valores por defecto
                const initialFilters = {
                ...selectedFilters,
                tipoRutina: defaultRoutineName, // <-- ahora es el nombre, no id
                cliente: defaultClientId
                };
                setSelectedFilters(initialFilters);

                // Cargar los datos del dashboard con los filtros iniciales
                fetchPlantData(initialFilters);

            } catch (err) {
                console.error('Error al obtener datos iniciales:', err);
                setError('No se pudieron cargar los datos iniciales.');
            }
        };

        fetchInitialData();
    }, [fetchPlantData]);



    return (
        <StyledMainContainer>
            <Header />
            <StyledHeaderWrapper>
                <StyledPageTitle variant="h4" component="h1" gutterBottom>
                    Home
                </StyledPageTitle>
            </StyledHeaderWrapper>

            {/* Botones de acción */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 4 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {/* Contenedor para el DayPicker */}
                    <TextField
                        label="Fecha Inicio"
                        variant="outlined"
                        size="small"
                        // Muestra la fecha 'from' formateada, si existe.
                        value={selectedFilters.selectedDate ? formatDateToYYYYMMDD(selectedFilters.selectedDate) : formatDateToYYYYMMDD(new Date())}
                        onClick={() => setIsCalendarOpen(true)} // Abre el calendario al hacer click
                        InputProps={{ readOnly: true }} // Hace el campo de solo lectura
                    />
                    {isCalendarOpen && (
                        <StyledPaper sx={{ position: 'absolute', zIndex: 1, mt: 10, p: 2 }}>
                            <DayPicker
                                mode="single" // Permite seleccionar solo un día
                                selected={selectedFilters.selectedDate}
                                onSelect={(date) => setSelectedFilters({ ...selectedFilters, selectedDate: date })}
                                showOutsideDays
                            />
                            <Button onClick={() => setIsCalendarOpen(false)} variant="outlined" sx={{ mt: 1 }}>
                                Cerrar
                            </Button>
                        </StyledPaper>
                    )}

                    <FormControl sx={{ minWidth: 180 }} size="small">
                        <InputLabel id="routine-type-label">Tipo de Rutina</InputLabel>
                        <Select
                            labelId="routine-type-label"
                            id="routine-type-select"
                            value={selectedFilters.tipoRutina}
                            label="Tipo de Rutina"
                            onChange={(e) => setSelectedFilters({ ...selectedFilters, tipoRutina: e.target.value })}
                        >
                            <MenuItem value="">
                                <em>Ninguno</em>
                            </MenuItem>
                            {tiposRutina.map((nombre) => (
                                <MenuItem key={nombre} value={nombre}>
                                    {nombre}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 180 }} size="small">
                        <InputLabel id="cliente-label">Cliente</InputLabel>
                        <Select
                            labelId="cliente-label"
                            id="cliente-select"
                            value={selectedFilters.cliente}
                            label="Cliente"
                            onChange={(e) => setSelectedFilters({ ...selectedFilters, cliente: e.target.value })}
                        >
                            <MenuItem value="">
                                <em>Todos los Clientes</em>
                            </MenuItem>
                            {clientes.map((cliente) => (
                                <MenuItem key={cliente.id} value={cliente.id}>
                                    {cliente.nombre}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="contained" color="primary" onClick={handleConsultarClick}>
                        Consultar
                    </Button>
                </Box>
            </Box>

            {/* Tarjetas de estadísticas */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap', backgroundColor: '#f9f9f9' }}>
                    <Paper elevation={1} sx={{ p: 2, textAlign: 'center', minWidth: 120, flexGrow: 1, borderRadius: 2, backgroundColor: '#e8f5e9' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#4caf50' }}>{stats.verde}</Typography>
                        <Typography variant="subtitle1" color="text.secondary">Verde</Typography>
                    </Paper>
                    <Paper elevation={1} sx={{ p: 2, textAlign: 'center', minWidth: 120, flexGrow: 1, borderRadius: 2, backgroundColor: '#fffde7' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#ffeb3b' }}>{stats.amarillo}</Typography>
                        <Typography variant="subtitle1" color="text.secondary">Amarillo</Typography>
                    </Paper>
                    <Paper elevation={1} sx={{ p: 2, textAlign: 'center', minWidth: 120, flexGrow: 1, borderRadius: 2, backgroundColor: '#ffebee' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#f44336' }}>{stats.rojo}</Typography>
                        <Typography variant="subtitle1" color="text.secondary">Rojo</Typography>
                    </Paper>
                    <Paper elevation={1} sx={{ p: 2, textAlign: 'center', minWidth: 120, flexGrow: 1, borderRadius: 2, border: '1px solid #ccc' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#666' }}>{stats.pendiente}</Typography>
                        <Typography variant="subtitle1" color="text.secondary">Pendiente</Typography>
                    </Paper>
                </Box>
            )}

            {/* Tabla de Plantas y Mapa */}
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 12 }}>

                    <Paper elevation={1} sx={{ p: 0.5, height: '100%', minHeight: '400px', borderRadius: 1 }}> {/* Ajusta padding y minHeight */}
                        {loadError && <Alert severity="error">Error al cargar Google Maps: {loadError.message}</Alert>}
                        {isLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <CircularProgress />
                            </Box>
                        )}
                        {!isLoading && isLoaded && (
                            <GoogleMap
                                mapContainerStyle={mapContainerStyle}
                                center={defaultCenter}
                                zoom={4.7} // Nivel de zoom inicial
                            >
                                {/* Marcadores para cada planta */}
                                {rutinas.map(rutina => {
                                    const direccion = direcciones.find(d => {
                                        // Agregamos la impresión de las IDs aquí
                                        //console.log(`Comparando con d.planta_id: ${d.planta_id}`);
                                        //console.log(`Buscando dirección para planta_id: ${rutina.planta_id}`);
                                        return d.planta_id === rutina.planta_id;
                                    });
                                    //console.log(direccion);
                                    if (direccion && direccion.latitude && direccion.longitud && rutina.status == 'respondida') {
                                        return (
                                            <Marker
                                                key={rutina.id}
                                                position={{ lat: direccion.latitude, lng: direccion.longitud }}
                                                title={rutina.planta.nombre}
                                                icon={{
                                                    url: getSemaforoColor(rutina.semaforo),
                                                    scaledSize: new window.google.maps.Size(30, 30)
                                                }}
                                            />
                                        );
                                    } else if (direccion && direccion.latitude && direccion.longitud) {
                                        return (
                                            <Marker
                                                key={rutina.id}
                                                position={{ lat: direccion.latitude, lng: direccion.longitud }}
                                                title={rutina.planta.nombre}
                                                icon={{
                                                    url: getSemaforoColor(rutina.status),
                                                    scaledSize: new window.google.maps.Size(30, 30)
                                                }}
                                            />
                                        );

                                    }
                                    return null;
                                })}
                            </GoogleMap>
                        )}
                    </Paper>

                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            label="Buscar en la tabla..."
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Box>
                    <StyledTableContainer component={StyledPaper} elevation={1} sx={{ maxHeight: '400px', overflow: 'auto' }}> {/* O component={Paper} si no estilizaste Paper */}
                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableHeaderCell>No.</StyledTableHeaderCell>
                                        <StyledTableHeaderCell>Planta</StyledTableHeaderCell>
                                        <StyledTableHeaderCell>Rutina</StyledTableHeaderCell>
                                        <StyledTableHeaderCell>Coordinador</StyledTableHeaderCell>
                                        <StyledTableHeaderCell>Respondida</StyledTableHeaderCell>
                                        <StyledTableHeaderCell>Semaforo</StyledTableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRutinas.length > 0 ? (
                                        filteredRutinas.map((rutina, index) => (
                                            <TableRow key={rutina.id}>
                                                <TableCell>{index + 1}</TableCell> {/* Numeración incremental */}
                                                <TableCell sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                                    {rutina.planta.nombre}
                                                </TableCell>
                                                <TableCell>{rutina.nombre}</TableCell>
                                                <TableCell>{rutina.coordinador.nombre}</TableCell>
                                                <TableCell>{rutina.status}</TableCell>
                                                {rutina.status == 'respondida' ? (
                                                    <TableCell>{rutina.semaforo}</TableCell>
                                                ) : (<TableCell> </TableCell>)
                                                }


                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>No se encontraron plantas.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </StyledTableContainer>
                </Grid>
            </Grid>

        </StyledMainContainer>
    );
}

export default HomeDashboard;